import { createSelector } from 'reselect';
import _ from 'lodash';
import dayjs from '../../../../utils/dayjs';

export const requestBrokerOrgsSelector = (state) => state.brokerReducer.requestBrokerOrgs;

export const loadingBrokerSelector = (state) => state.brokerReducer.loadingBroker;

export const brokerLookupCompleteSelector = (state) => state.brokerReducer.brokerLookupComplete;

export const brokerInfoSelector = (state) => state.brokerReducer.brokerInfo;

export const loadingBrokersSelector = (state) => state.brokerReducer.loadingBrokers;

export const brokersSelector = (state) => state.brokerReducer.brokers;

export const loadingBrokerageSelector = (state) => state.brokerReducer.loadingBrokerage;

export const brokerageInfoSelector = (state) => state.brokerReducer.brokerageInfo;

export const brokerIdSelector = createSelector(brokerInfoSelector, (brokerInfo) =>
  brokerInfo ? brokerInfo.brokerId : null,
);

export const subscriptionsSelector = (state) => state.brokerReducer.brokerageSubscriptions;

export const loadingBrokerageSubscriptionsSelector = (state) => state.brokerReducer.loadingBrokerageSubscriptions;

const normalizeSummary = (raw) => {
  if (!raw) return null;
  const unAssigned = raw.unAssigned ?? 0;
  const remaining = raw.remaining ?? unAssigned;
  const assigned = raw.assigned ?? 0;
  const expired = raw.expired ?? 0;
  const unUsedPromoCodes = raw.unUsedPromoCodes ?? raw.unusedPromo ?? 0;
  const promoCodes = raw.promoCodes ?? 0;
  const expiredPromoCodes = raw.expiredpromoCodes ?? 0;
  const revokedUnUsedPromoCodes = raw.revokedUnUsedPromoCodes ?? 0;
  return {
    total: raw.total ?? 0,
    remaining,
    unAssigned,
    assigned,
    expired,
    promoCodes,
    unUsedPromoCodes,
    expiredPromoCodes,
    revokedUnUsedPromoCodes,
    subscriptions: assigned,
    revoked: expired,
    unusedPromo: unUsedPromoCodes,
    sent: unUsedPromoCodes,
    promoCodeCount: promoCodes,
  };
};

const isNullDate = (date) => !date || new Date(date).getFullYear() <= 1;

export const brokerageSummarySelector = createSelector(subscriptionsSelector, (subscriptions) =>
  subscriptions ? normalizeSummary(subscriptions.summary) : null,
);

export const brokerageSubscriptionsSelector = createSelector(
  subscriptionsSelector,
  brokersSelector,
  (subscriptions, brokers) =>
    subscriptions && Array.isArray(subscriptions.subscriptions)
      ? _.chain(subscriptions.subscriptions)
          .map((item) => {
            const broker = item.assignedById ? _.find(brokers, { brokerId: item.assignedById }) : null;
            const brokerName = broker ? `${broker.firstName} ${broker.lastName}` : null;
            const activeDate = item.activeDate ? dayjs(item.activeDate).format('DD/MM/YYYY') : null;
            const exactExpiryDate = isNullDate(item.expiryDate) ? null : item.expiryDate;
            const expiryDate = exactExpiryDate ? dayjs(item.expiryDate).format('DD/MM/YYYY') : null;
            let isExpired = item.status === 'Expired';
            if (!isExpired && exactExpiryDate && !item.isRevoked) {
              isExpired = dayjs(item.expiryDate).isBefore(dayjs());
            }
            return { ...item, brokerName, activeDate, expiryDate, exactExpiryDate, isExpired };
          })
          .value()
      : undefined,
);

export const brokerageSubscriptionsExpiredCountSelector = createSelector(
  subscriptionsSelector,
  brokerageSubscriptionsSelector,
  (raw, subscriptions) => {
    if (raw?.summary?.expired != null) {
      return raw.summary.expired;
    }
    if (!subscriptions) {
      return 0;
    }
    return subscriptions.filter((item) => item.isExpired).length;
  },
);

export const brokerageIdSelector = createSelector(brokerInfoSelector, (brokerInfo) =>
  brokerInfo ? brokerInfo.brokerageId : null,
);

export const brokerEmailSelector = createSelector(brokerInfoSelector, (brokerInfo) =>
  brokerInfo ? brokerInfo.email : null,
);

export const promoCodesSelector = createSelector(subscriptionsSelector, brokersSelector, (subscriptions, brokers) => {
  return subscriptions && Array.isArray(subscriptions.promoCodes)
    ? _.chain(subscriptions.promoCodes)
        .map((item) => {
          const broker = _.find(brokers, { brokerId: item.createdById });
          const brokerName = broker ? `${broker.firstName} ${broker.lastName}` : item.createdBy || null;
          const rawExpiryDate = item.promoExpiryDate ?? item.expiryDate;
          const expiryDate = isNullDate(rawExpiryDate) ? null : dayjs(rawExpiryDate).format('DD/MM/YYYY');
          const exactExpiryDate = isNullDate(rawExpiryDate) ? null : rawExpiryDate;
          let isExpired = false;
          if (!item.isRevoked && !isNullDate(rawExpiryDate)) {
            isExpired = new Date(rawExpiryDate).getTime() < new Date().getTime();
          }
          const unAssigned = item.unAssigned ?? item.unassigned ?? item.remaining ?? 0;
          const isRevoked =
            Boolean(item.isRevoked) || String(item.status ?? '').trim().toLowerCase() === 'revoked';
          const promoCodeId = item.promoCodeId ?? item.promoCodeid ?? item.PromoCodeId ?? item.id ?? null;
          const code = item.promoCode ?? item.code ?? null;
          const promoCodeRegistrationLink =
            item.promoCodeRegistrationLink ?? item.link ?? null;
          return {
            ...item,
            isRevoked,
            promoCodeId,
            code,
            promoCode: code,
            promoCodeRegistrationLink,
            link: promoCodeRegistrationLink,
            brokerName,
            expiryDate,
            exactExpiryDate,
            isExpired,
            unAssigned,
          };
        })
        .value()
    : undefined;
});

export const promoCodesExpiredSelector = createSelector(
  subscriptionsSelector,
  promoCodesSelector,
  (raw, promoCodes) => {
    if (raw?.summary?.expiredpromoCodes != null) {
      return raw.summary.expiredpromoCodes;
    }
    if (!promoCodes) {
      return 0;
    }
    return promoCodes.filter((item) => item.isExpired).length;
  },
);

export const availableKeysSelector = createSelector(subscriptionsSelector, (subscriptions) =>
  subscriptions && Array.isArray(subscriptions.availableKeys) ? subscriptions.availableKeys : [],
);
